import { AnyAction, Dispatch } from 'redux';
import apis from '../../utils/apis';
import { handleApiError } from './utils/utils';
import { showFeedbackModal } from './UserFeedbackActions';
import { DATA_SOURCE_ADD_COMPLETED, DATA_SOURCE_ADD_CONNECTED, DATA_SOURCE_ADD_FAILED, DATA_SOURCE_ADD_STEP, DATA_SOURCE_ADD_STEPS, DATA_SOURCE_CREATE_COMPLETED, DATA_SOURCE_CREATE_CONNECTED,  DATA_SOURCE_CREATE_FAILED, DATA_SOURCE_CREATE_RESET, DATA_SOURCE_CREATE_STEP, DATA_SOURCE_CREATE_STEPS, SET_DATA_SOURCES } from '../types';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from '..';

export const FETCH_DATA_SOURCES_REQUEST = 'FETCH_DATA_SOURCES_REQUEST';
export const FETCH_DATA_SOURCES_FAILURE = 'FETCH_DATA_SOURCES_FAILURE';

export const setDataSources = (payload: { indexes: any[]; total: number; currentPage: number; totalPages: number }) => ({
  type: SET_DATA_SOURCES,
  payload
});

export const fetchDataSources = (params: {
  searchTerm?: string;
  filters?: { [key: string]: string[] };
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
  includeArchived?: boolean;
} = {}) => async (dispatch: Dispatch<any>) => {
  dispatch({ type: FETCH_DATA_SOURCES_REQUEST }); 
  try {
    const {
      searchTerm = '',
      sortBy = 'created_at',
      sortOrder = 'desc',
      page = 1,
      limit = 50,
      includeArchived = false,
    } = params;

    const queryParams = new URLSearchParams({
      page: String(page),
      page_size: String(limit),
      include_archived: String(includeArchived),
      sort_by: sortBy,
      sort_order: sortOrder,
    });
    if (searchTerm) queryParams.set('search_term', searchTerm);

    const resp = await apis({
      method: 'GET',
      url: `/datasources?${queryParams.toString()}`,
    });

    if (resp.status === 200) {
      const { datasources = [], total = 0, page: currentPage = 1, total_pages: totalPages = 1 } = resp.data;
      dispatch(setDataSources({ indexes: datasources, total, currentPage, totalPages }));
      return { dataSources: datasources, total, currentPage, totalPages };
    } else {
      dispatch({ type: FETCH_DATA_SOURCES_FAILURE, payload: 'Failed to fetch data sources.' });
      handleApiError(resp, dispatch);
      return null;
    }
  } catch (error: any) {
    dispatch({ type: FETCH_DATA_SOURCES_FAILURE, payload: error.message || 'An error occurred.' });
    handleApiError(error, dispatch);
    return null;
  }
};

export const addDataToDataSourceSSE =
  (dataSourceUuid: string, files: File[]) =>
    async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
      dispatch({ type: DATA_SOURCE_ADD_CONNECTED });
      
      try {
        // Initialize steps with proper structure
        dispatch({ 
          type: DATA_SOURCE_ADD_STEPS, 
          payload: { 
            steps: [
              { id: 'upload', label: 'Uploading files', status: 'pending' },
              { id: 'index', label: 'Indexing', status: 'pending' }
            ] 
          } 
        });
        dispatch({ type: DATA_SOURCE_ADD_STEP, payload: { id: 'upload', status: 'in_progress', message: `Uploading ${files.length} files...` } });
        
        // Bulk upload all files at once
        const fd = new FormData();
        files.forEach((file, i) => {
          fd.append(`file${i}`, file, file.name);
        });
        
        await apis({
          method: 'POST',
          url: `/datasources/${dataSourceUuid}/files/bulk`,
          data: fd,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        
        dispatch({ type: DATA_SOURCE_ADD_STEP, payload: { id: 'upload', status: 'completed', message: `${files.length} files uploaded` } });
        
        // Trigger reindex
        dispatch({ type: DATA_SOURCE_ADD_STEP, payload: { id: 'index', status: 'in_progress', message: 'Starting index update...' } });
        
        await apis({
          method: 'POST',
          url: `/datasources/${dataSourceUuid}/reindex`,
        });
        
        dispatch({ type: DATA_SOURCE_ADD_STEP, payload: { id: 'index', status: 'completed', message: 'Index job queued' } });
        
        dispatch({ type: DATA_SOURCE_ADD_COMPLETED, payload: { ok: true } });
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: 'Files added and indexed successfully.',
            modalType: 'success',
            duration: 2500,
          })
        );
        
        return { status: 'success' };
      } catch (err: any) {
        dispatch({ type: DATA_SOURCE_ADD_FAILED, payload: { error: String(err) } });
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: err.message || 'Failed to add files.',
            modalType: 'error',
            duration: 3500,
          })
        );
        throw err;
      }
    };


export const getDataSourceInfo = (dataSourceUuid: string) => async (dispatch: Dispatch<any>) => {
  try {
    const resp = await apis({
      method: 'GET',
      url: `/datasources/${dataSourceUuid}`,
    });

    if (resp.status === 200) {
      // Transform backend response to frontend format
      const data = resp.data;
      const transformed = {
        id: data.uuid,
        DataSourceName: data.name,
        DataSourceType: data.visibility === 'private' ? 'Personal' : data.visibility === 'organization' ? 'Company' : 'Shared',
        last_index_run: data.last_indexed_at,
        last_indexed_at: data.last_indexed_at,
        last_synced_at: data.last_synced_at,
        CreatedAt: data.created_at,
        created_at: data.created_at,
        updated_at: data.updated_at,
        document_count: data.document_count,
        total_storage_size: formatBytes(data.total_size_bytes),
        total_size_bytes: data.total_size_bytes,
        language: data.language,
        description: data.description,
        type: data.type,
        indexing_status: data.indexer_status || 'notStarted',
        indexer_status: data.indexer_status,
        keywords: data.keywords || [],
        collaborators: data.collaborators || [],
        shared_with: data.collaborators || [],
        status: data.status,
        visibility: data.visibility,
        files: data.files || [],
        sync_error: data.sync_error,
        blob_path: data.blob_path,
        allowed_file_types: data.allowed_file_types || [],
        llm_generated_at: data.llm_generated_at,
      };
      return { status: 'success', data: transformed };
    } else {
      throw resp;
    }
  } catch (error) {
    handleApiError(error, dispatch);
  }
};

// Helper to format bytes
const formatBytes = (bytes?: number) => {
  if (!bytes && bytes !== 0) return undefined;
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let i = -1;
  let size = bytes;
  do {
    size = size / 1024;
    i++;
  } while (size >= 1024 && i < units.length - 1);
  return `${size.toFixed(1)} ${units[i]}`;
};


export const listFilesInDataSource = (dataSourceUuid: string) => async (dispatch: Dispatch<any>) => {
  try {
    const resp = await apis({
      method: 'GET',
      url: `/datasources/${dataSourceUuid}/files`,
    });

    if (resp.status === 200) {
      return { status: 'success', data: { files: resp.data.files || [] } };
    } else {
      throw resp;
    }
  } catch (error) {
    handleApiError(error, dispatch);
  }
};


export const deleteFileFromDataSource = (dataSourceUuid: string, fileId: string) => async (dispatch: Dispatch<any>) => {
  try {
    const resp = await apis({
      method: 'DELETE',
      url: `/datasources/${dataSourceUuid}/files/${fileId}`,
    });

    if (resp.status === 200) {
      const reindexing = resp.data?.reindexing;
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: reindexing
            ? 'File deleted. Reindexing data source…'
            : (resp.data.message || 'File deleted successfully'),
          modalType: 'success',
          duration: 3000,
        })
      );
      return resp.data;
    } else {
      throw resp;
    }
  } catch (error) {
    handleApiError(error, dispatch);
  }
};


/**
 * Download a file from a data source as a Blob.
 * Returns an object URL that can be passed to the side panel viewer.
 */
export const downloadDataSourceFile =
  (dataSourceUuid: string, fileId: string) =>
  async (dispatch: Dispatch<any>): Promise<{ url: string; contentType: string } | undefined> => {
    try {
      const resp = await apis({
        method: 'GET',
        url: `/datasources/${dataSourceUuid}/files/${fileId}/download`,
        responseType: 'blob',
      });

      if (resp.status === 200) {
        const blob = resp.data as Blob;
        const objectUrl = URL.createObjectURL(blob);
        const contentType = resp.headers?.['content-type'] || blob.type || 'application/octet-stream';
        return { url: objectUrl, contentType };
      } else {
        throw resp;
      }
    } catch (error) {
      handleApiError(error, dispatch);
      return undefined;
    }
  };


export const deleteDataSource = (dataSourceUuid: string) => async (dispatch: Dispatch<any>) => {
  try {
    const resp = await apis({
      method: 'DELETE',
      url: `/datasources/${dataSourceUuid}?hard=true`,
    });

    if (resp.status === 200) {
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: resp.data.message || 'Data source deleted successfully',
          modalType: 'success',
          duration: 3000,
        })
      );
      dispatch(fetchDataSources());
      return { status: 'success' };
    } else {
      throw resp;
    }
  } catch (error) {
    handleApiError(error, dispatch);
  }
};


export const deleteDataSourcesBulk = (datasourceUuids: string[]) => async (dispatch: Dispatch<any>) => {
  try {
    const resp = await apis({
      method: 'POST',
      url: '/datasources/delete_bulk',
      data: { datasource_uuids: datasourceUuids },
    });

    if (resp.status === 200) {
      const { deleted_count, total_requested, failed } = resp.data;
      const allSucceeded = deleted_count === total_requested;
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: allSucceeded
            ? `${deleted_count} data source${deleted_count > 1 ? 's' : ''} deleted successfully`
            : `${deleted_count} of ${total_requested} data sources deleted${failed?.length ? `. ${failed.length} failed.` : ''}`,
          modalType: allSucceeded ? 'success' : 'warning',
          duration: 3000,
        })
      );
      dispatch(fetchDataSources());
      return { status: 'success', data: resp.data };
    } else {
      throw resp;
    }
  } catch (error) {
    handleApiError(error, dispatch);
  }
};


export const chatWithDataSource = (dataSourceUuid: string, prompt: string, history?: { role: string; content: string }[]) => async (dispatch: Dispatch<any>) => {
  try {
    const resp = await apis({
      method: 'POST',
      url: `/datasources/${dataSourceUuid}/chat`,
      data: { prompt, history },
    });

    if (resp.status === 200) {
      return resp.data.response || resp.data.ChatResponse;
    } else {
      throw resp;
    }
  } catch (error) {
    handleApiError(error, dispatch);
  }
};

export const createDataSourceSSE =
  (dataSourceName: string, files: File[], dataSourceType: string, sharedWith?: string[], projectUuid?: string) =>
    async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
      dispatch({ type: DATA_SOURCE_CREATE_CONNECTED });
      
      try {
        const visibility = dataSourceType === 'Personal' ? 'private' : 
                          dataSourceType === 'Company' ? 'organization' : 'shared';
        
        // Initialize steps matching the AI Engine pipeline
        dispatch({ 
          type: DATA_SOURCE_CREATE_STEPS, 
          payload: { 
            steps: [
              { id: 'validate', label: 'Validating Data Source', status: 'pending' },
              { id: 'search_index', label: 'Creating Search Index', status: 'pending' },
              { id: 'process_docs', label: 'Processing Documents', status: 'pending' },
              { id: 'metadata', label: 'Generating Metadata', status: 'pending' },
              { id: 'finalize', label: 'Finalizing', status: 'pending' },
            ] 
          } 
        });
        
        // Build multipart form data with files + metadata in a single request
        const fd = new FormData();
        fd.append('name', dataSourceName);
        fd.append('visibility', visibility);
        if (sharedWith && sharedWith.length > 0) {
          fd.append('shared_with', JSON.stringify(sharedWith));
        }
        if (projectUuid) {
          fd.append('project_uuid', projectUuid);
        }
        files.forEach((file, i) => {
          fd.append(`file${i}`, file, file.name);
        });
        
        // Single unified POST — creates record, uploads files, queues AI processing
        const resp = await apis({
          method: 'POST',
          url: '/datasources/create',
          data: fd,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        
        if (resp.status !== 201) {
          throw new Error(resp.data?.error || 'Failed to create data source');
        }
        
        const { datasource, processing, pubsub } = resp.data;
        const dataSourceUuid = datasource?.uuid || processing?.datasourceUuid;
        const correlationId = processing?.correlationId;
        
        // Return info for PubSub connection (form component handles the live progress)
        return { 
          status: 200, 
          data: { 
            uuid: dataSourceUuid, 
            correlationId,
            pubsub,
          } 
        };
      } catch (err: any) {
        dispatch({ type: DATA_SOURCE_CREATE_FAILED, payload: { error: String(err) } });
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: err.message || 'Failed to create data source.',
            modalType: 'error',
            duration: 4000,
          })
        );
        throw err;
      }
    };


export const resetDataSourceCreation = () => (dispatch: any) => {
  dispatch({ type: DATA_SOURCE_CREATE_RESET });
};


/**
 * Update data source details (name, description, type, keywords, visibility).
 */
export const updateDataSource =
  (dataSourceUuid: string, updates: { name?: string; description?: string; type?: string; keywords?: string[]; visibility?: string }) =>
    async (dispatch: Dispatch<any>) => {
      try {
        const resp = await apis({
          method: 'PUT',
          url: `/datasources/${dataSourceUuid}`,
          data: updates,
        });

        if (resp.status === 200) {
          dispatch(
            showFeedbackModal({
              showModal: true,
              message: 'Data source updated successfully.',
              modalType: 'success',
              duration: 2500,
            })
          );
          return { status: 'success', data: resp.data };
        } else {
          throw resp;
        }
      } catch (error) {
        handleApiError(error, dispatch);
        return { status: 'error' };
      }
    };


type SharedEntry = { id: string; email: string, name: string, initials: string, avatarUrl: string, role: string,  access?: "view" | "edit" } | string;
export const updateDataSourceSharedAccess =
  (dataSourceUuid: string, sharedWith: SharedEntry[]) =>
    async (dispatch: Dispatch<any>) => {
      try {
        const resp = await apis({
          method: "PUT",
          url: `/datasources/${dataSourceUuid}`,
          data: {
            shared_with: sharedWith,
          },
        });

        if (resp.status === 200) {
          // dispatch(
          //   showFeedbackModal({
          //     showModal: true,
          //     message: "Shared access updated.",
          //     modalType: "success",
          //     duration: 2500,
          //   })
          // );

          return resp.data?.collaborators || resp.data?.shared_with || [];
        } else {
          throw resp;
        }
      } catch (error) {
        handleApiError(error, dispatch);
        throw error;
      }
    };

    export const revokeSharedAccess = (dataSourceUuid: string, userId: string) => async (dispatch: Dispatch<any>) => {
      try {
        const resp = await apis({
          method: 'DELETE',
          url: `/datasources/${dataSourceUuid}/access/${userId}`,
        });
    
        if (resp.status === 200) {
          dispatch(
            showFeedbackModal({
              showModal: true,
              message: resp.data?.message || 'Access revoked successfully',
              modalType: 'success',
              duration: 3000,
            })
          );
          return resp.data;
        } else {
          throw resp;
        }
      } catch (error) {
        handleApiError(error, dispatch);
      }
    };

